import { useState, useEffect } from 'react';
import { useOwnerAdmin } from '../../context/OwnerAdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Brain, DollarSign, TrendingUp, Users, Activity,
  RefreshCw, ArrowUpRight, ArrowDownRight, Minus,
  Zap, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ title, value, subtitle, icon: Icon, trend, color = "emerald" }) {
  return (
    <Card data-testid={`ai-stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold text-foreground">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-xl bg-${color}-500/10 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${color}-500`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 mt-2 text-xs">
            {trend > 0 ? <ArrowUpRight className="w-3 h-3 text-emerald-500" /> :
             trend < 0 ? <ArrowDownRight className="w-3 h-3 text-red-500" /> :
             <Minus className="w-3 h-3 text-muted-foreground" />}
            <span className={trend > 0 ? "text-emerald-500" : trend < 0 ? "text-red-500" : "text-muted-foreground"}>
              {Math.abs(trend)}% vs last period
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function OwnerAdminAIUsage() {
  const { token } = useOwnerAdmin();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/owner-admin/ai-usage/report`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch AI usage report');
      const data = await res.json();
      setReport(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="ai-usage-loading">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">AI Usage & Cost Report</h1>
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <Card key={i}><CardContent className="p-5 h-24 animate-pulse bg-muted/50" /></Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="ai-usage-error">
        <Card><CardContent className="p-8 text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={fetchReport} variant="outline" size="sm">Retry</Button>
        </CardContent></Card>
      </div>
    );
  }

  const s = report?.summary || {};
  const daily = report?.daily_usage || [];
  const topUsers = report?.top_users || [];

  // Find max sessions in daily data for chart scaling
  const maxDaily = Math.max(...daily.map(d => d.sessions), 1);

  return (
    <div className="space-y-6 p-6" data-testid="ai-usage-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Brain className="w-6 h-6 text-violet-500" />
            AI Usage & Cost Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            DSCR Counseling sessions — cost tracking and margin analysis
          </p>
        </div>
        <Button onClick={fetchReport} variant="outline" size="sm" className="gap-2" data-testid="ai-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Pricing Config Banner */}
      <Card className="border-violet-500/20 bg-violet-500/5">
        <CardContent className="p-4 flex flex-wrap items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-violet-500/30 text-violet-600">Model</Badge>
            <span className="text-foreground font-medium">GPT-4o-mini</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-blue-500/30 text-blue-600">Cost/Session</Badge>
            <span className="text-foreground font-medium">${s.cost_per_session?.toFixed(4)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-emerald-500/30 text-emerald-600">Overage Rate</Badge>
            <span className="text-foreground font-medium">${s.overage_rate?.toFixed(3)}/session</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-amber-500/30 text-amber-600">Markup</Badge>
            <span className="text-foreground font-medium">{s.markup_multiplier}x</span>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Sessions"
          value={s.total_sessions?.toLocaleString() || '0'}
          subtitle={`${s.sessions_today || 0} today · ${s.sessions_7d || 0} this week`}
          icon={Activity}
          color="violet"
        />
        <StatCard
          title="Platform Cost (30d)"
          value={`$${(s.cost_30d || 0).toFixed(2)}`}
          subtitle={`$${(s.total_platform_cost || 0).toFixed(2)} all time`}
          icon={DollarSign}
          color="red"
        />
        <StatCard
          title="Overage Revenue (30d)"
          value={`$${(s.revenue_30d || 0).toFixed(2)}`}
          subtitle={`$${(s.total_user_charges || 0).toFixed(2)} all time`}
          icon={TrendingUp}
          color="emerald"
        />
        <StatCard
          title="Net Margin (30d)"
          value={`$${(s.margin_30d || 0).toFixed(2)}`}
          subtitle={`${s.total_sessions > 0 ? ((s.total_overage / s.total_sessions * 100) || 0).toFixed(1) : 0}% sessions were overage`}
          icon={Zap}
          color="amber"
        />
      </div>

      {/* Session Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Free Sessions</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-foreground">{s.total_free?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Included in subscription</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Overage Sessions</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-foreground">{s.total_overage?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Charged at ${s.overage_rate?.toFixed(3)}/session</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">All-Time Margin</CardTitle></CardHeader>
          <CardContent>
            <p className={`text-3xl font-bold ${(s.total_margin || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              ${(s.total_margin || 0).toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Revenue minus platform cost</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Usage Chart (simple bar chart) */}
      {daily.length > 0 && (
        <Card data-testid="ai-daily-chart">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-violet-500" />
              Daily Sessions (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-[2px] h-32">
              {daily.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                  {/* Overage portion */}
                  {d.overage > 0 && (
                    <div
                      className="w-full bg-amber-500 rounded-t-sm min-h-[1px]"
                      style={{ height: `${(d.overage / maxDaily) * 100}%` }}
                    />
                  )}
                  {/* Free portion */}
                  <div
                    className={`w-full bg-violet-500 ${d.overage > 0 ? '' : 'rounded-t-sm'} rounded-b-sm min-h-[1px]`}
                    style={{ height: `${(d.free / maxDaily) * 100}%` }}
                  />
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 bg-popover text-popover-foreground border border-border rounded-lg p-2 text-xs whitespace-nowrap shadow-lg">
                    <p className="font-medium">{d.date}</p>
                    <p>{d.sessions} sessions ({d.free} free, {d.overage} overage)</p>
                    <p>Cost: ${d.platform_cost.toFixed(4)} · Revenue: ${d.user_charges.toFixed(4)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
              <span>{daily[0]?.date}</span>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-violet-500" /> Free</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> Overage</span>
              </div>
              <span>{daily[daily.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Users Table */}
      <Card data-testid="ai-top-users">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-500" />
            Top Users by AI Sessions (Last 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No AI usage data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">User</th>
                    <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Sessions</th>
                    <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Free</th>
                    <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Overage</th>
                    <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Your Cost</th>
                    <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Charged</th>
                    <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.map((u, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/50">
                      <td className="py-2.5 px-3">
                        <p className="font-medium text-foreground">{u.name || 'No Name'}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </td>
                      <td className="text-right py-2.5 px-3 font-medium text-foreground">{u.sessions}</td>
                      <td className="text-right py-2.5 px-3 text-violet-500">{u.free}</td>
                      <td className="text-right py-2.5 px-3">
                        {u.overage > 0 ? <span className="text-amber-500">{u.overage}</span> : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="text-right py-2.5 px-3 text-red-500">${u.platform_cost.toFixed(4)}</td>
                      <td className="text-right py-2.5 px-3 text-emerald-500">${u.user_charges.toFixed(4)}</td>
                      <td className="text-right py-2.5 px-3 font-medium">
                        <span className={(u.user_charges - u.platform_cost) >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                          ${(u.user_charges - u.platform_cost).toFixed(4)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tier Allocation Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">AI Session Allocation by Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { plan: "Starter", price: "$19.99", sessions: 25, color: "blue" },
              { plan: "Professional", price: "$19.99", sessions: 75, color: "violet" },
              { plan: "Business", price: "$34.99", sessions: 200, color: "emerald" },
            ].map(t => (
              <div key={t.plan} className="p-4 rounded-xl border border-border bg-muted/30">
                <p className="font-medium text-foreground">{t.plan} <span className="text-muted-foreground font-normal">({t.price}/mo)</span></p>
                <p className={`text-2xl font-bold text-${t.color}-500 mt-1`}>{t.sessions}</p>
                <p className="text-xs text-muted-foreground">free sessions/month</p>
                <p className="text-xs text-muted-foreground mt-1">Then $0.002/session</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
